import { useArrayFilter, useArrayFind, useArrayMap } from "@vueuse/core";
import { useBuiltComponents } from "src/shared/store/component-built/composables";
import { computed, toValue } from "vue";
import { ExtensionComponentAdapter } from "./ExtensionComponentAdapter";
const useExtensionComponents = () => {
    const { builtComponents, primer } = useBuiltComponents();
    const extensionComponents = useArrayFilter(builtComponents, (component) => component.types.includes("EXTENSION"));
    return {
        extensionComponents,
        primer,
    };
};
const useExtoleExtensionComponents = () => {
    const { extensionComponents, primer } = useExtensionComponents();
    const nonRootExtensionComponents = useArrayFilter(extensionComponents, (component) => component.owner === "EXTOLE" && !component.isRoot());
    const extoleExtensionComponents = useArrayFilter(nonRootExtensionComponents, (component) => {
        const sameNameComponents = nonRootExtensionComponents.value.filter((nonRootExtensionComponent) => nonRootExtensionComponent.name === component.name);
        const hasNewTypeComponent = sameNameComponents.some((newTypeComponent) => {
            var _a, _b;
            return ((_a = newTypeComponent.type) === null || _a === void 0 ? void 0 : _a.startsWith("extension-")) ||
                ((_b = newTypeComponent.type) === null || _b === void 0 ? void 0 : _b.startsWith("extension."));
        });
        if (component.type === "extension" && hasNewTypeComponent) {
            return false;
        }
        return true;
    });
    return {
        extoleExtensionComponents,
        primer,
    };
};
const useClientExtensionComponents = () => {
    const { extensionComponents, primer } = useExtensionComponents();
    const clientExtensionComponents = useArrayFilter(extensionComponents, (component) => component.owner === "CLIENT" && !component.isRoot());
    return {
        clientExtensionComponents,
        primer,
    };
};
export const useExtensions = () => {
    const { extoleExtensionComponents, primer: builtComponentsPrimer } = useExtoleExtensionComponents();
    const extensions = useArrayMap(extoleExtensionComponents, (component) => new ExtensionComponentAdapter(component));
    return {
        extensions,
        primer: builtComponentsPrimer,
    };
};
export const useInstalledExtensions = () => {
    const { clientExtensionComponents, primer } = useClientExtensionComponents();
    const extensions = useArrayMap(clientExtensionComponents, (component) => new ExtensionComponentAdapter(component));
    return {
        extensions,
        primer,
    };
};
export const useAllExtensions = () => {
    const { extensionComponents, primer } = useExtensionComponents();
    const extensions = useArrayMap(extensionComponents, (component) => new ExtensionComponentAdapter(component));
    return {
        extensions,
        primer,
    };
};
export const useInstalledExtension = (id) => {
    const idValue = toValue(id);
    const { extensions, primer } = useInstalledExtensions();
    const extension = useArrayFind(extensions, (extension) => {
        return extension.getID() === idValue;
    });
    return {
        extension,
        primer,
    };
};
export const useExtensionRootComponents = () => {
    const { extoleExtensionComponents, primer: builtComponentsPrimer } = useExtoleExtensionComponents();
    const extensionRootComponents = useArrayFilter(extoleExtensionComponents, (component) => component.isRoot());
    return {
        extensionRootComponents,
        primer: builtComponentsPrimer,
    };
};
export const useExtensionRootComponent = (campaignId) => {
    if (!campaignId) {
        return {
            component: undefined,
        };
    }
    const { extensionRootComponents } = useExtensionRootComponents();
    const component = useArrayFind(extensionRootComponents, (component) => {
        return component.campaign_id === campaignId;
    });
    return {
        component,
    };
};
export const useInstalledExtensionComponent = (id) => {
    const idValue = toValue(id);
    const { clientExtensionComponents, primer } = useClientExtensionComponents();
    if (idValue === undefined) {
        return {
            component: undefined,
            primer,
        };
    }
    const component = useArrayFind(clientExtensionComponents, (component) => {
        return component.id === idValue;
    });
    return {
        component,
        primer,
    };
};
export const useExtension = (id) => {
    const idValue = toValue(id);
    const { extensions, primer } = useAllExtensions();
    const extension = useArrayFind(extensions, (extension) => {
        return extension.getID() === idValue;
    });
    return {
        extension,
        primer,
    };
};
export const useExtensionTypeCategories = () => {
    const { extensions } = useExtensions();
    const typeCategories = useArrayMap(extensions, (extension) => {
        return extension.getTypeCategories();
    });
    const uniqueTypeCategories = computed(() => {
        return [...new Set(typeCategories.value.flat())];
    });
    return {
        typeCategories: uniqueTypeCategories,
    };
};
export const useExtensionFunctionalityCategories = () => {
    const { extensions } = useExtensions();
    const functionalityCategories = useArrayMap(extensions, (extension) => {
        return extension.getFunctionalityCategories();
    });
    const uniqueFunctionalityCategories = computed(() => {
        return [...new Set(functionalityCategories.value.flat())];
    });
    return {
        functionalityCategories: uniqueFunctionalityCategories,
    };
};
export const useExtensionCategories = () => {
    const { typeCategories } = useExtensionTypeCategories();
    const { functionalityCategories } = useExtensionFunctionalityCategories();
    const categories = computed(() => {
        return [...typeCategories.value, ...functionalityCategories.value];
    });
    const uniqueCategories = computed(() => {
        return [...new Set(categories.value)];
    });
    return {
        categories: uniqueCategories,
    };
};
